// GET /links · GET /links/:id · DELETE /links/:id · GET /links/limits
import { z } from 'zod'
import {
  genderSchema,
  linkTypeSchema,
  moodValueSchema,
  nodeStateSchema,
  tierSchema,
} from '../enums.js'
import { cityDtoSchema } from './city.js'
import {
  idSchema,
  isoDateSchema,
  isoDateTimeSchema,
  okResponseSchema,
  timeOfDaySchema,
} from './common.js'
import { moodDaySchema } from './mood.js'

// Другая сторона связи — минимум, нужный для карточки узла и для подстановки в формы.
//
// Данные рождения (`birth_date`, `birth_time`, `birth_city`) отдаются сознательно: ими
// предзаполняются ряд «из твоих уз» в совместимости и в натальной карте. Раньше отдавалась
// одна дата, и подстановка получалась половинчатой — форма заполнялась датой, а время и город
// человек дописывал руками за другого, по памяти. С тех пор как время и город реально участвуют
// в расчёте (docs/domain.md §4.1), цена этой половинчатости — не удобство, а число: без них
// синастрия считается на полдень и без асцендента.
//
// Это осознанный сдвиг обещания спеки 07 §3 («связи видят отметки и неделю»): узу человек
// заводит сам и вручную, она взаимна и разрывается в один тап, — но текст экрана приватности
// обязан называть этот сдвиг вслух, иначе мы обещаем одно, а отдаём другое.
// Отдаётся только по установленной взаимной связи: чужой человек этого не увидит.
//
// `gender` — не «данные», а грамматика: без него Луня говорит о человеке в женском роде
// («Дима не показывалась»). Связь всегда соединяет двух зарегистрированных людей, поле у обоих
// уже есть; `null` — человек пол не указал, тексты берут нейтральную формулировку.
export const linkPartnerSchema = z.object({
  id: idSchema,
  first_name: z.string(),
  gender: genderSchema.nullable(),
  photo_url: z.string().nullable(),
  birth_date: isoDateSchema.nullable(),
  /** Время рождения, `HH:mm`. `null` — человек его не указывал. */
  birth_time: timeOfDaySchema.nullable(),
  /**
   * Город рождения целиком, а не один `id`: подстановка обязана показать название сразу,
   * а не после похода в справочник — иначе поле города выглядит пустым при заполненном черновике.
   */
  birth_city: cityDtoSchema.nullable(),
})
export type LinkPartner = z.infer<typeof linkPartnerSchema>

// Связь, как её видит текущий пользователь. `tier` — сырая ступень по теплоте (см.
// domain/teplo.ts), `state` — состояние узла с учётом оверрайдов (маячок/отпуск важнее ступени),
// используется domain/layout.ts для расстановки. Цифры теплоты в UI не показываются, но
// нужны фронту для оптимистичного пересчёта после лучика.
/**
 * Лучик, который ещё «летит» по нити на полотне уз.
 *
 * Свежесть — `RECENT_RAY_TTL_HOURS`: ровно столько, сколько обещает копирайт шторки лучика.
 * Число в контракте и число в тексте обязаны совпадать, менять их только вместе.
 */
export const recentRaySchema = z.object({
  /** Текст комплимента; `null` — лучик без слов, он тоже долетает. */
  text: z.string().nullable(),
  created_at: isoDateTimeSchema,
})
export type RecentRay = z.infer<typeof recentRaySchema>

/** Сколько часов лучик считается свежим и летит по нити. */
export const RECENT_RAY_TTL_HOURS = 6

export const linkDtoSchema = z.object({
  id: idSchema,
  type: linkTypeSchema,
  label: z.string(), // ярлык связи с точки зрения текущего пользователя ("мама", "дочка" …)
  partner: linkPartnerSchema,
  teplo: z.number().min(0).max(1),
  tier: tierSchema,
  state: nodeStateSchema,
  last_mood_value: moodValueSchema.nullable(), // для текстового лейбла "Сияю/Норм/Тяжко" на фронте
  vacation_until: isoDateSchema.nullable(),
  days_together: z.number().int().min(0),
  last_ray_at: isoDateTimeSchema.nullable(),
  /** Последний свежий лучик ОТ МЕНЯ этому человеку. */
  recent_ray_out: recentRaySchema.nullable(),
  /** Последний свежий лучик ОТ НЕГО мне. */
  recent_ray_in: recentRaySchema.nullable(),
  created_at: isoDateTimeSchema,
})
export type LinkDto = z.infer<typeof linkDtoSchema>

// GET /links
export const listLinksResponseSchema = z.object({
  items: z.array(linkDtoSchema),
})
export type ListLinksResponse = z.infer<typeof listLinksResponseSchema>

// Краткая совместимость для экрана человека / шита узла.
export const linkCompatBriefSchema = z.object({
  score: z.number().int().min(30).max(100),
  vibe: z.string(), // короткая строка вайба из курируемого пула
})
export type LinkCompatBrief = z.infer<typeof linkCompatBriefSchema>

// GET /links/:id — расширенная карточка: неделя и совместимость.
//
// Поля `ray_available_at` здесь больше нет: часовой кулдаун на одного человека снят (решение
// основателя), единственное ограничение лучиков — дневное (`domain/limits.ts`), и оно живёт
// в `GET /links/limits`.
export const linkDetailDtoSchema = linkDtoSchema.extend({
  week: z.array(moodDaySchema),
  compat: linkCompatBriefSchema.nullable(),
})
export type LinkDetailDto = z.infer<typeof linkDetailDtoSchema>
export const getLinkResponseSchema = linkDetailDtoSchema
export type GetLinkResponse = z.infer<typeof getLinkResponseSchema>

// DELETE /links/:id — hard delete, без уведомления второй стороне.
export const deleteLinkResponseSchema = okResponseSchema
export type DeleteLinkResponse = z.infer<typeof deleteLinkResponseSchema>

// GET /links/limits — лимиты бесплатной версии: связи и лучики (docs/domain.md §4).
// `limit: null` и `rays_limit: null` означают «без ограничения» — так выглядит подписка.
//
// Чисел про связи здесь ДВА, и путать их дорого — на этом уже был баг «0 из 5 уз, хотя одна
// связь есть». Счётчик над полотном отвечает на вопрос «сколько у меня уз», а лимит — на
// вопрос «сколько я ещё могу позвать», и с тех пор как приглашение действительно создаёт
// связь, эти два ответа расходятся: вход по чужой ссылке гостевой и слот не тратит
// (`GUEST_LINK_COUNTS_AGAINST_LIMIT`, 04_links.md §2.9). Одним числом их не описать.
export const linkLimitsResponseSchema = z.object({
  /**
   * Сколько уз у человека всего — ровно то, что он видит на полотне: активные связи
   * (независимо от того, кто кого позвал) плюс собственные неотвеченные приглашения.
   * Это число показывает счётчик и карточка уз на главной.
   */
  used: z.number().int().min(0),
  /**
   * Сколько из лимита занято. Считает **только исходящие** (04_links.md §2.9): связи,
   * созданные мной, плюс мои неотвеченные приглашения. Гостевые узы сюда не входят —
   * по этому числу гаснет «Добавить связь» и краснеет счётчик.
   */
  slots_used: z.number().int().min(0),
  limit: z.number().int().min(0).nullable(),
  /** Сколько лучиков отправлено за текущие сутки (полночь по UTC+3). */
  rays_used: z.number().int().min(0),
  rays_limit: z.number().int().min(0).nullable(),
  /** Когда обнулится дневной счётчик лучиков. */
  rays_reset_at: isoDateTimeSchema,
})
export type LinkLimitsResponse = z.infer<typeof linkLimitsResponseSchema>
